import type {
  FertilityProjectionResult,
  ProjectionInput,
  ProjectionEstimate,
} from "./types";

const SEMEN_SOURCE_ID = "villani-semen-art-outcomes-2022";
const ENDOMETRIOSIS_SOURCE_ID = "muteshi-endometriosis-art-outcomes-2018";
const ADENOMYOSIS_SOURCE_ID = "li-adenomyosis-ivf-outcomes-2022";

export interface AppliedProjectionModifier {
  id: string;
  label: string;
  modelId: string;
  sourceIds: string[];
  inputIds: string[];
  reason: string;
}

/**
 * Applies only effect sizes whose endpoint and ART method are explicit in the
 * source. These are transportable associations, not clinic-calibrated causal
 * effects, so the result remains pending clinical review.
 */
export function applySourceBackedModifiers(
  input: ProjectionInput,
  projection: FertilityProjectionResult,
): {
  projection: FertilityProjectionResult;
  applications: AppliedProjectionModifier[];
} {
  let probability = projection.probabilityAtLeastOneLiveBirth;
  const applications: AppliedProjectionModifier[] = [];
  const assumptions = [...projection.assumptions];
  const warnings = [...projection.warnings];

  if (probability) {
    input.semen?.forEach((sample, index) => {
      const method = sample.fertilizationMethod?.value;
      const source = sample.source?.value;
      const morphology = sample.morphologyPercent?.value;
      const progressiveMotility = sample.progressiveMotilityPercent?.value;
      if (source === "donor" || source === "surgical") return;
      if (method === "icsi" && Number.isFinite(morphology)) {
        const current = probability;
        if (!current) return;
        probability = applyOddsRatio(
          current,
          morphology! - 4,
          1.018,
          1.013,
          1.023,
        );
        applications.push({
          id: `semen.${index}.morphology`,
          label: "Sperm morphology",
          modelId: "villani-2022-icsi-live-birth",
          sourceIds: [SEMEN_SOURCE_ID],
          inputIds: [`semen.${index}.morphology`],
          reason:
            "Villani et al. reported an adjusted live-birth odds ratio of 1.018 per percentage point of normal morphology in ICSI cycles; the effect is centered at 4% and transported here only when the fertilization method is explicitly ICSI.",
        });
      } else if (method === "ivf" && Number.isFinite(progressiveMotility)) {
        const current = probability;
        if (!current) return;
        probability = applyOddsRatio(
          current,
          progressiveMotility! - 32,
          1.005,
          1.001,
          1.007,
        );
        applications.push({
          id: `semen.${index}.progressive_motility`,
          label: "Progressive sperm motility",
          modelId: "villani-2022-ivf-live-birth",
          sourceIds: [SEMEN_SOURCE_ID],
          inputIds: [`semen.${index}.progressive_motility`],
          reason:
            "Villani et al. reported an adjusted live-birth odds ratio of 1.005 per percentage point of progressive motility in IVF cycles; the effect is centered at the WHO fifth-edition reference of 32% and transported here only when the fertilization method is explicitly IVF.",
        });
      }
    });
  }

  const medicalFactors = input.patient.medicalFactors?.value ?? [];
  if (
    probability &&
    (projection.kind === "embryo_transfer" ||
      input.context?.treatmentIntent === "ivf" ||
      input.context?.treatmentIntent === "icsi")
  ) {
    if (
      medicalFactors.some(
        (factor) => normalizeFactor(factor) === "endometriosis",
      )
    ) {
      probability = applyOddsRatio(probability, 1, 0.76, 0.59, 0.98);
      applications.push({
        id: "patient.medical_factors.endometriosis",
        label: "Endometriosis",
        modelId: "muteshi-2018-endometriosis-live-birth",
        sourceIds: [ENDOMETRIOSIS_SOURCE_ID],
        inputIds: ["patient.medical_factors"],
        reason:
          "Muteshi et al. reported lower live-birth odds for endometriosis versus unexplained subfertility in an ART cohort; the adjusted association is transported only to an ART/embryo-transfer target.",
      });
    }
    if (
      medicalFactors.some((factor) => normalizeFactor(factor) === "adenomyosis")
    ) {
      probability = applyOddsRatio(probability, 1, 0.541, 0.39, 0.751);
      applications.push({
        id: "patient.medical_factors.adenomyosis",
        label: "Adenomyosis",
        modelId: "li-2022-adenomyosis-live-birth",
        sourceIds: [ADENOMYOSIS_SOURCE_ID],
        inputIds: ["patient.medical_factors"],
        reason:
          "Li et al. reported an adjusted live-birth odds ratio of 0.541 for adenomyosis in a fresh IVF/embryo-transfer cohort; the association is transported only to an ART/embryo-transfer target.",
      });
    }
  }

  if (applications.length > 0) {
    assumptions.push(
      "Semen and disease effects are source-backed associations transported across populations; they require clinic calibration and are applied to the live-birth probability target, not to every intermediate stage count.",
    );
    warnings.push(
      "One or more modifier effect sizes came from observational ART cohorts and are pending clinical review for this population and clinic.",
    );
  }

  const sourceIds = [
    ...projection.sourceIds,
    ...applications.flatMap((application) => application.sourceIds),
  ];
  return {
    projection: {
      ...projection,
      assumptions: [...new Set(assumptions)],
      warnings: [...new Set(warnings)],
      sourceIds: [...new Set(sourceIds)],
      probabilityAtLeastOneLiveBirth: probability,
    },
    applications,
  };
}

function applyOddsRatio(
  estimate: ProjectionEstimate,
  delta: number,
  meanOddsRatio: number,
  lowOddsRatio: number,
  highOddsRatio: number,
): ProjectionEstimate {
  const adjusted = [
    adjustProbability(estimate.mean, delta, meanOddsRatio),
    adjustProbability(
      estimate.low,
      delta,
      delta >= 0 ? lowOddsRatio : highOddsRatio,
    ),
    adjustProbability(
      estimate.high,
      delta,
      delta >= 0 ? highOddsRatio : lowOddsRatio,
    ),
  ];
  return {
    mean: adjusted[0] ?? estimate.mean,
    low: Math.min(adjusted[1] ?? estimate.low, adjusted[2] ?? estimate.high),
    high: Math.max(adjusted[1] ?? estimate.low, adjusted[2] ?? estimate.high),
    unit: "probability",
  };
}

function adjustProbability(
  probability: number,
  delta: number,
  oddsRatio: number,
): number {
  const bounded = Math.max(0.000001, Math.min(0.999999, probability));
  const odds = bounded / (1 - bounded);
  const adjustedOdds = odds * Math.pow(oddsRatio, delta);
  return Math.max(0, Math.min(1, adjustedOdds / (1 + adjustedOdds)));
}

function normalizeFactor(factor: string): string {
  return factor.trim().toLowerCase().replaceAll("_", " ");
}
